import React, { useState, useRef } from 'react';
import CodeContextMenu from './CodeContextMenu';
import './CodeBlock.css';

const CodeBlock = ({ 
  children, 
  className = '', 
  language = '', 
  inline = false,
  onCopy,
  onDownload 
}) => {
  const [contextMenu, setContextMenu] = useState({
    isVisible: false,
    position: { x: 0, y: 0 }
  });
  const codeRef = useRef(null);

  // Extract language from className (e.g., "language-r" -> "r")
  const detectedLanguage = language || 
    (className ? className.replace('language-', '') : '') ||
    'text';

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const x = e.clientX;
    const y = e.clientY;
    
    // Ensure menu stays within viewport
    const menuWidth = 280;
    const menuHeight = 300;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    const adjustedX = x + menuWidth > viewportWidth ? x - menuWidth : x;
    const adjustedY = y + menuHeight > viewportHeight ? y - menuHeight : y;
    
    setContextMenu({
      isVisible: true,
      position: { x: adjustedX, y: adjustedY }
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu({
      isVisible: false,
      position: { x: 0, y: 0 }
    });
  };

  const handleCopy = (code) => {
    if (onCopy) {
      onCopy(code);
    } else {
      navigator.clipboard.writeText(code).then(() => {
        // Show success notification
        showNotification('Code copied to clipboard!');
      }).catch(() => {
        showNotification('Failed to copy code', 'error');
      });
    }
  };

  const handleDownload = (code, lang) => {
    if (onDownload) {
      onDownload(code, lang);
    } else {
      const blob = new Blob([code], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `code_${Date.now()}.${lang || 'txt'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showNotification('Code downloaded!');
    }
  };

  const showNotification = (message, type = 'success') => {
    const notification = document.createElement('div');
    notification.className = `code-notification ${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'error' ? '#dc3545' : '#28a745'};
      color: white;
      padding: 12px 20px;
      border-radius: 4px;
      z-index: 10000;
      font-size: 14px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.2);
      animation: notificationSlideIn 0.3s ease-out;
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 3000);
  };

  // Get the code content as a string
  const getCodeContent = () => {
    if (typeof children === 'string') {
      return children;
    }
    
    if (React.isValidElement(children)) {
      return children.props.children || '';
    }
    
    if (Array.isArray(children)) {
      return children.map(child => 
        typeof child === 'string' ? child : 
        React.isValidElement(child) ? child.props.children || '' : 
        String(child)
      ).join('');
    }
    
    return String(children || '');
  };

  const codeContent = getCodeContent();

  if (inline) {
    return (
      <code 
        className={`code-inline ${className}`}
        onContextMenu={handleContextMenu}
        ref={codeRef}
        title="Right-click for options"
      >
        {children}
        <CodeContextMenu
          code={codeContent}
          language={detectedLanguage}
          isVisible={contextMenu.isVisible}
          position={contextMenu.position}
          onClose={handleCloseContextMenu}
          onCopy={handleCopy}
          onDownload={handleDownload}
        />
      </code>
    );
  }

  return (
    <div className="code-block-wrapper">
      <pre 
        className={`code-block ${className} code-block-interactive`}
        onContextMenu={handleContextMenu}
        ref={codeRef}
        title="Right-click for options"
      >
        <code className={`language-${detectedLanguage}`}>
          {children}
        </code>
      </pre>
      
      <div className="code-block-header">
        <span className="code-language">{detectedLanguage}</span>
        <div className="code-block-actions">
          <button 
            className="code-action-btn"
            onClick={() => handleCopy(codeContent)}
            title="Copy code"
          >
            📋
          </button>
          <button 
            className="code-action-btn"
            onClick={() => handleDownload(codeContent, detectedLanguage)}
            title="Download code"
          >
            💾
          </button>
        </div>
      </div>
      
      <CodeContextMenu
        code={codeContent}
        language={detectedLanguage}
        isVisible={contextMenu.isVisible}
        position={contextMenu.position}
        onClose={handleCloseContextMenu}
        onCopy={handleCopy}
        onDownload={handleDownload}
      />
    </div>
  );
};

export default CodeBlock;
